import type { ReactElement } from 'react';
import React, { Fragment } from 'react';
import { css, keyframes } from '@compiled/react';
import throttle from 'lodash/throttle';

import { WidthObserver } from '@atlaskit/width-detector';

import type { CardSizes } from '../linkCardsTypes';

import { useCardWidthAndSpacingCalculations } from './useCardWidthAndSpacingCalculations';
import { CardWrapper, cardsDimensions } from './CardOrientationWrapper';
import { MIN_GAP_BETWEEN_CARDS } from './CardsOrientation';

const wrapperStyles = css({
	overflowX: 'hidden',
	display: 'flex',
	// We render the card set twice for autoscroll, so applying a hover animation pause directly to one group won't stop the other.
	// Instead, we detect hover on the entire parent/container to pause the animation for all child elements at once.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover > div': {
		animationPlayState: 'paused',
	},
});

const scrollAnimation = keyframes({
	'0% ': {
		transform: 'translateX(0)',
	},
	'100%': {
		transform: 'translateX(-100%)',
	},
});

const cardsGroupStyles = css({
	display: 'flex',
});

const animationStyles = css({
	animation: `${scrollAnimation} linear infinite`,
});

const shortDuration = css({
	animationDuration: '40s',
});

const mediumDuration = css({
	animationDuration: '50s',
});

const longDuration = css({
	animationDuration: '110s',
});

type AutoscrollWrapperProps = {
	numberOfCards: number;
	cardsSize: CardSizes;
	children: ReactElement[];
};

export const AutoscrollWrapper = ({
	numberOfCards,
	cardsSize,
	children,
}: AutoscrollWrapperProps) => {
	const { calculations, setCurrentCardsGridWidth } = useCardWidthAndSpacingCalculations({
		numberOfCards,
		cardsSize,
	});

	const throttledSetWidth = throttle(setCurrentCardsGridWidth, 200);

	const { cardWidthOnFullRow, isFirstRowOverflown: isAnimationEnabled } = calculations;

	const renderCardsGroup = () => (
		<div
			css={[
				cardsGroupStyles,
				isAnimationEnabled && animationStyles,
				numberOfCards <= 5 ? shortDuration : numberOfCards <= 10 ? mediumDuration : longDuration,
			]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			style={{ gap: `${MIN_GAP_BETWEEN_CARDS}px`, paddingRight: `${MIN_GAP_BETWEEN_CARDS}px` }}
		>
			{children.map((child, cardIndex) => {
				return (
					<Fragment key={cardIndex}>
						<CardWrapper
							isFirstRowFull
							cardWidthOnFullRow={cardWidthOnFullRow}
							cardMinWidth={cardsDimensions[cardsSize].minWidth}
							cardMaxWidth={cardsDimensions[cardsSize].maxWidth}
						>
							{child}
						</CardWrapper>
					</Fragment>
				);
			})}
		</div>
	);

	// we are making a duplicate call to renderCardsGroup to make the infinte scroll experience more seamless
	// duplicate call is only required when animation is enabled, meaning there is overflow of cards than the number of cards a row can fit
	// in which case, no autoscroll will take effect
	return (
		<div css={wrapperStyles} data-testid="cards-autoscroll-wrapper">
			{renderCardsGroup()}
			{isAnimationEnabled && renderCardsGroup()}
			<WidthObserver setWidth={throttledSetWidth} offscreen />
		</div>
	);
};
